import * as Sentry from '@sentry/react';
import { HStack, useDisclosure } from '@televet/kibble-ui';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { BaseEmoji } from 'emoji-mart';
import 'emoji-mart/css/emoji-mart.css';
import useKeyboardShortcuts from 'pages/Conversations/hooks/useKeyboardShortcuts';
import { IAttachmentOption } from 'pages/Conversations/interfaces/IAttachmentOption';
import { TAvailableKeyboardShortcutChars } from 'pages/Conversations/interfaces/IKeyboardShortcutElementRefs';
import { getChannelParticipants } from 'pages/Conversations/utils';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { ElementIds } from 'shared/enums/ElementIds';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import { WildcardValues } from 'shared/enums/WildcardOptions';
import useClinicUser from 'shared/hooks/useClinicUser';
import usePersistedState from 'shared/hooks/usePersistedState';
import usePrevious from 'shared/hooks/usePrevious';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import {
  ChannelMessageAttachmentType,
  ChannelStatusAction,
  ChannelViewChannelFragment,
  ChannelViewChannelMessageFragment,
  CreateChannelMessageMutation,
  MessageType,
} from 'shared/types/graphql';
import { colonsToUnicode } from 'shared/utils';
import styled from 'styled-components/macro';
import useChannelDraft from '../../../hooks/useChannelDraft';
import { useSendMessage } from '../../../hooks/useSendMessage';
import { ArchivedOverlay } from './ArchivedOverlay';
import MessageComposerAttachment from './Attachments';
import LinkPetToFormModal from './Attachments/Form/LinkPetToFormModal';
import OpenFormFieldModal from './Attachments/Form/OpenFormFieldModal';
import MessageComposerKeyboardShortcutHelpModal, { availableKeyboardShortcuts } from './KeyboardShortcutHelpModal';
import MentionsList, { IMentionedUser } from './Mentions/MentionsList';
import useQuickReplyUsageTracking from './QuickReply/hooks/useQuickReplyUsageTracking';
import { useTracking } from 'shared/providers/TrackingProvider';
import QuickReplyPopover from './QuickReply/QuickReplyPopover';
import MessageComposerToolbar from './Toolbar';
import DictationButton from './Toolbar/DictationButton';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import SendAsChatCheckbox from './SendAsChatCheckbox';

interface MessageComposerProps {
  channel?: ChannelViewChannelFragment | null;
  isChannelLoading: boolean;
  isTeamChannel: boolean;
  isDirectTeamMessage: boolean;
  channelLastMessage: ChannelViewChannelMessageFragment | null;
  defaultDraftMessageText?: string;
  setPreviewData: (fileUrl: string, attachmentType: ChannelMessageAttachmentType, fileName: string) => void;
  setCurrentInvoiceId: (invoiceId: string) => void;
  invoiceClinicPetParentId: string;
  setAttachmentFeePercent: (percent: number) => void;
  attachments: IAttachmentOption[];
  setAttachments: (
    attachments: ((prevAttachments: IAttachmentOption[]) => IAttachmentOption[]) | IAttachmentOption[],
  ) => void;
  updateInvoiceAttachmentDiscount: (invoiceId: string, discount: number) => void;
  setAttachInvoiceClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  isOverlay?: boolean;
  onChannelMessageCreated?: (data: CreateChannelMessageMutation) => void;
}

const MessageComposer = ({
  channel,
  isChannelLoading,
  isTeamChannel,
  isDirectTeamMessage,
  channelLastMessage,
  defaultDraftMessageText,
  setPreviewData,
  setCurrentInvoiceId,
  setAttachmentFeePercent,
  attachments,
  setAttachments,
  setAttachInvoiceClicked,
  isOverlay,
  onChannelMessageCreated,
}: MessageComposerProps): JSX.Element => {
  const [sendAsChatMessage, setSendAsChatMessage] = useState(false);
  const { removeInvoiceAttachment, setRemoveInvoiceAttachment, invoiceId } = useSidePanelSearch();
  const [draftMessageText, setDraftMessageText] = useState('');
  const draftMessageTextRef = useRef<string>('');
  const { clinicUser, currentClinicId, currentClinic } = useClinicUser();
  const { allChannelPets, channelPetParents, channelPetParentIds } = getChannelParticipants(channel?.channelMembers);
  const linkedPets = useMemo(
    () => (channel?.pets === undefined || channel?.pets.length === 0 ? allChannelPets : channel.pets),
    [channel?.pets, allChannelPets],
  );
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [messageType, setMessageType] = useState<MessageType>(MessageType.Message);
  const {
    isOpen: isShortcutHelpModalOpen,
    onOpen: openShortcutHelpModal,
    onClose: closeShortcutHelpModal,
  } = useDisclosure();
  const {
    isOpen: isOpenFormFieldModalOpen,
    onOpen: onOpenFormFieldModalOpen,
    onClose: onOpenFormFieldModalClose,
  } = useDisclosure();
  const {
    isOpen: isInvoicingDrawerOpen,
    onClose: onInvoicingDrawerClose,
    onToggle: onInvoicingDrawerToggle,
  } = useDisclosure();
  const { trackEvent } = useTracking();

  const inputTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const invoiceAmountInputRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();
  const { isMobile } = useResolutionProvider();
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [hasPotentialBenefits, setHasPotentialBenefits] = useState(false);
  const [invoiceForSidePanelId, setInvoiceForSidePanelId] = useState<string>('');
  const [isLinkPetToFormModalOpen, setIsLinkPetToFormModalOpen] = useState(false);
  const [selectedFormAttachment, setSelectedFormAttachment] = useState<IAttachmentOption | undefined>();
  const [shouldOpenOpenFormModal, setShouldOpenOpenFormModal] = useState(false);
  const [isDictationEnabled] = usePersistedState('isDictationEnabled', false);
  const channelId = channel?.id;

  /**
   * Focus on message input when switching channel or message type
   */
  const { addSuggestion, discardSuggestion, handleMessageSent, setSelectedSuggestion } =
    useQuickReplyUsageTracking(channelId);

  useEffect(() => {
    if (inputTextareaRef.current && channelId && !isMobile) {
      inputTextareaRef.current.focus();
    }
  }, [inputTextareaRef, channelId, messageType, isMobile]);

  const { channelDraft, setChannelDraft, clearChannelDraft } = useChannelDraft({ channelId: channelId || '' });

  const resetToPersistedDraftState = useCallback((): void => {
    const { messageType, draftMessageText, attachments } = channelDraft;
    setMessageType(messageType);
    setDraftMessageText(defaultDraftMessageText ?? draftMessageText);
    setAttachments(attachments);
    setStagedFiles([]);
  }, [channelDraft, defaultDraftMessageText, setAttachments]);

  /**
   * When channel ID changes, populate inputs with draft values from local storage
   */
  const previousChannelId = usePrevious(channelId);
  useEffect(() => {
    if (channelId && channelId !== previousChannelId) {
      resetToPersistedDraftState();
    }
  }, [channelId, previousChannelId, resetToPersistedDraftState]);

  /**
   * Handle @ mentions
   */
  const [mentionsListIsVisible, setMentionsListIsVisible] = useState<boolean>(false);
  const [mentionedUsersDraft, setMentionedUsersDraft] = useState<IMentionedUser[] | []>([]);
  const shouldShowMentionsList = (messageType === MessageType.Note || isTeamChannel) && !isDirectTeamMessage;

  const handleTeamMembersMentionList = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const fullMessageText = e.currentTarget.value;

    if (!fullMessageText.includes('@')) {
      setMentionsListIsVisible(false);
      return;
    }
  }, []);

  const handleDraftMessageTextChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const message = event.target.value;
      const unicodeMessage = colonsToUnicode(event.target.value);
      if (message !== unicodeMessage) {
        trackEvent(ConversationEvents.ChannelEmojiTypedManually, { clinicId: currentClinicId });
      }
      if (!isSendingMessage) {
        setDraftMessageText(unicodeMessage);
      }

      if (shouldShowMentionsList && mentionsListIsVisible) {
        handleTeamMembersMentionList(event);
      }
    },
    [
      isSendingMessage,
      shouldShowMentionsList,
      mentionsListIsVisible,
      trackEvent,
      currentClinicId,
      handleTeamMembersMentionList,
    ],
  );

  /**
   * Handle keyboard shortcuts
   */
  const shortcuts = useKeyboardShortcuts(['a', 'e', 'f', 'g', 'h', 'i', 't']);

  useEffect(() => {
    shortcuts.subscribe('h', openShortcutHelpModal);

    return (): void => {
      shortcuts.unsubscribe('h', openShortcutHelpModal);
    };
  }, [openShortcutHelpModal, shortcuts]);

  const handleKeyboardShortcuts = useCallback(
    (value: string) => {
      const shortcutIndex = value.indexOf('//');
      const selectedShortcut = value.slice(shortcutIndex + 2, shortcutIndex + 3) as TAvailableKeyboardShortcutChars;

      // Remove shortcut from message text
      setDraftMessageText((value) => `${value.slice(0, shortcutIndex)}${value.slice(shortcutIndex + 3)}`);

      // Throw warning if not a valid shortcut

      if (!shortcuts.isValidKey(selectedShortcut)) {
        toast({
          status: 'info',
          title: "Whoops! That shortcut doesn't exist. Try '//h' to view a list of all available shortcuts",
        });
        trackEvent(ConversationEvents.ChannelInvalidShortcutUsed);
        return;
      } else {
        shortcuts.trigger(selectedShortcut);
      }

      const shortcutName = availableKeyboardShortcuts.filter((shortcut) => shortcut.char === selectedShortcut)[0].name;
      trackEvent(ConversationEvents.ChannelShortcutUsed, { shortcutName });
    },
    [shortcuts, trackEvent, toast],
  );

  /**
   * - Store message draft on ref to prevent unnecessary re-renders
   * - Ensure draft stored in local storage is kept in sync
   */
  useEffect(() => {
    draftMessageTextRef.current = draftMessageText;
    if (!isSendingMessage) {
      setChannelDraft({ draftMessageText });
    }
  }, [draftMessageText, draftMessageTextRef, isSendingMessage, setChannelDraft]);

  /**
   * Store changes to messageType or attachments in local storage
   */
  useEffect(() => {
    setChannelDraft({
      messageType,
      attachments,
      fillInTheBlankFormValues:
        channelDraft?.fillInTheBlankFormValues?.filter((draftValues) =>
          attachments.some(({ id }) => id === draftValues.attachmentEntityId),
        ) || [],
    });
    // avoid circular rerender when adding channelDraft as dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, messageType, setChannelDraft]);

  const handleEmojiSelect = useCallback(
    (emoji: BaseEmoji): void => {
      if (!inputTextareaRef.current) return;

      trackEvent(ConversationEvents.ChannelEmojiSelectedFromDrawer);
      const selectionStart = inputTextareaRef.current.selectionStart;
      const selectionEnd = inputTextareaRef.current.selectionEnd;
      setDraftMessageText((value) => `${value.slice(0, selectionStart)}${emoji.native}${value.slice(selectionEnd)}`);
      const emojiLength = emoji.native.length;
      const newStart = selectionStart + emojiLength;
      const newEnd = selectionEnd + emojiLength;

      inputTextareaRef.current.setSelectionRange(newStart, newEnd);

      setIsEmojiPickerOpen(!isEmojiPickerOpen);
      inputTextareaRef.current?.focus();
      setTimeout(() => {
        inputTextareaRef.current?.setSelectionRange(newStart, newEnd);
      }, 0);
    },
    [trackEvent, isEmojiPickerOpen],
  );

  const onError = useCallback(
    (error: Error): void => {
      console.error(error);
      Sentry.captureException(error);
      toast({
        status: 'error',
        title: error.message,
      });
    },
    [toast],
  );

  const onAttachmentChange = useCallback(
    (attachment: IAttachmentOption) => {
      setAttachments((prevAttachments) => prevAttachments.map((a) => (a.id === attachment.id ? attachment : a)));
    },
    [setAttachments],
  );

  const onAttachmentRemove = useCallback(
    (attachment: IAttachmentOption) => {
      setAttachments((prevAttachments) => {
        const attachments = [...prevAttachments];
        const index = attachments.findIndex((a) => a.id === attachment.id);
        const removal = attachments.splice(index, 1).pop();
        if (removal?.value?.filename) {
          const newFiles = stagedFiles.filter((file: File) => file?.name !== removal.value.filename);
          setStagedFiles(newFiles);
        }
        return attachments;
      });
      setIsLinkPetToFormModalOpen(false);
    },
    [setAttachments, stagedFiles],
  );

  const { sendMessage } = useSendMessage({
    channel,
    onError,
    onSuccess: onChannelMessageCreated,
  });

  const validateAttachments = useCallback((): IAttachmentOption[] => {
    const invalidAttachments = attachments.filter((attachment) => {
      return attachment.validationError;
    });
    return invalidAttachments;
  }, [attachments]);

  const handleSendMessage = useCallback(async (): Promise<void> => {
    if (isChannelLoading || !sendMessage || isSendingMessage) {
      // Do nothing
    } else {
      setIsSendingMessage(true);
      try {
        const trimmedMessage = draftMessageTextRef.current.trim();
        const invalidAttachments = validateAttachments();
        const mentionedUsers: IMentionedUser[] = [];

        // Ensure mentioned users haven't been removed from message before notifying them
        mentionedUsersDraft.forEach((user: IMentionedUser) => {
          const userExists = mentionedUsers.find((mentionedUser) => mentionedUser.id === user.id);
          if (userExists) return;

          if (trimmedMessage.includes(`@${user.name}`)) {
            mentionedUsers.push(user);
          }
        });

        if (mentionedUsers.length > 0) {
          trackEvent(ConversationEvents.ChannelUserMentioned);
        }

        if (invalidAttachments.length) {
          for (const invalidAttachment of invalidAttachments) {
            toast({ title: invalidAttachment.validationError, status: 'error' });
          }
          setIsSendingMessage(false);
          return;
        }

        onInvoicingDrawerClose();

        // Replace {{fillInTheBlank}} fields with stored values
        const fillInTheBlankForms = attachments.filter((attachment) => attachment.isFillInTheBlankForm);
        if (fillInTheBlankForms.length) {
          const draftValues = channelDraft?.fillInTheBlankFormValues;

          // Ensure all fields have been given values and don't send message if not
          // Empty fields will just have empty string values in the draft state
          let firstIncompleteFormId = draftValues.find(({ inputValues }) =>
            inputValues?.some((values) => values.includes('')),
          )?.attachmentEntityId;

          // check for any FITB forms without draft data
          if (!firstIncompleteFormId) {
            firstIncompleteFormId = fillInTheBlankForms.find(
              (attachment) => !draftValues.some(({ attachmentEntityId }) => attachmentEntityId === attachment.id),
            )?.id;
          }

          if (firstIncompleteFormId) {
            setIsSendingMessage(false);
            const firstIncompleteForm = attachments.find(({ id }) => id === firstIncompleteFormId);

            if (firstIncompleteForm) {
              setSelectedFormAttachment(firstIncompleteForm);
              onOpenFormFieldModalOpen();
            }

            return;
          }

          for (let i = 0; i < fillInTheBlankForms.length; i++) {
            const { id, formTemplateContent } = fillInTheBlankForms[i];
            const modifiedFormTemplateContent = { ...formTemplateContent };
            const modifiedQuestions = [...modifiedFormTemplateContent.questions];
            const blankValues =
              draftValues
                .find((values) => values.attachmentEntityId == id)
                ?.inputValues.flat()
                .filter(Boolean) || [];

            // Replace wildcards with saved values
            for (const value of blankValues) {
              const intro = modifiedFormTemplateContent.introMessage;
              if (intro.includes(WildcardValues.FillInTheBlank)) {
                modifiedFormTemplateContent.introMessage = intro.replace(WildcardValues.FillInTheBlank, value);
              } else {
                for (let j = 0; j < modifiedQuestions.length; j++) {
                  const question = modifiedQuestions[j];
                  if (question.prompt.includes(WildcardValues.FillInTheBlank)) {
                    const newQuestion = { ...modifiedQuestions[j] };
                    newQuestion.prompt = question.prompt.replace(WildcardValues.FillInTheBlank, value);
                    modifiedQuestions[j] = newQuestion;
                    break;
                  }
                }
              }
            }

            modifiedFormTemplateContent.questions = modifiedQuestions;
            fillInTheBlankForms[i].formTemplateContent = modifiedFormTemplateContent;
            onAttachmentChange(fillInTheBlankForms[i]);
          }
        }

        if (!!stagedFiles.length || !!attachments.length || !!trimmedMessage) {
          const result = await sendMessage({
            text: trimmedMessage,
            attachments,
            messageType,
            sendAsChatMessage,
            mentionedUsers,
          });
          if (result) {
            setDraftMessageText('');
            clearChannelDraft(channelId || '', { messageType });
            setStagedFiles([]);
            setAttachments([]);

            // AI messages handler
            handleMessageSent(result, trimmedMessage);
          }
        }
      } catch (e: unknown) {
        if (e instanceof Error) {
          toast({ title: e.message, status: 'error' });
        }
      }
      setIsSendingMessage(false);
    }
  }, [
    isChannelLoading,
    sendMessage,
    isSendingMessage,
    validateAttachments,
    mentionedUsersDraft,
    onInvoicingDrawerClose,
    attachments,
    stagedFiles.length,
    trackEvent,
    toast,
    channelDraft?.fillInTheBlankFormValues,
    onOpenFormFieldModalOpen,
    onAttachmentChange,
    messageType,
    sendAsChatMessage,
    clearChannelDraft,
    channelId,
    setAttachments,
    handleMessageSent,
  ]);

  const messageInputPlaceholder = useMemo(() => {
    if (messageType === MessageType.Note) {
      return isTeamChannel ? 'Make a note...' : `Make internal note or type '//h' for shortcuts...`;
    }
    return isTeamChannel
      ? `Type a message for ${channel?.friendlyName || 'your team'}...`
      : `Type a message or '//h' for shortcuts...`;
  }, [channel, isTeamChannel, messageType]);

  const [isMessageComposerExpanded, setIsMessageComposerExpanded] = usePersistedState(
    PersistedStateKeys.IsMessageComposerExpanded,
    false,
  );

  const handleComposerToggle = useCallback(() => {
    trackEvent(
      isMessageComposerExpanded
        ? ConversationEvents.ChannelComposerCollapsed
        : ConversationEvents.ChannelComposerExpanded,
    );
    setIsMessageComposerExpanded((value) => !value);
  }, [isMessageComposerExpanded, setIsMessageComposerExpanded, trackEvent]);

  useEffect(() => {
    if (removeInvoiceAttachment && !!attachments.length) {
      const attachment = attachments.find((attachment) => attachment.text && attachment.text === 'Invoice');
      if (attachment) {
        onAttachmentRemove(attachment);
      }
      setRemoveInvoiceAttachment(false);
    }
  }, [
    removeInvoiceAttachment,
    invoiceId,
    onAttachmentRemove,
    attachments,
    setRemoveInvoiceAttachment,
    invoiceForSidePanelId,
  ]);

  // Open OpenFormModal after LinkPetToFormModal is closed after attaching new form
  useEffect(() => {
    if ((!isLinkPetToFormModalOpen || !linkedPets) && shouldOpenOpenFormModal && selectedFormAttachment) {
      onOpenFormFieldModalOpen();
      setShouldOpenOpenFormModal(false);
    }
  }, [isLinkPetToFormModalOpen, linkedPets, onOpenFormFieldModalOpen, selectedFormAttachment, shouldOpenOpenFormModal]);

  const isConversationInactive = useMemo(() => {
    const channelStatus = channel?.channelStatus?.channelStatusAction;

    if (isTeamChannel) {
      return !channel?.isActive;
    }

    if (!channelStatus) return;

    return [ChannelStatusAction.Inactive, ChannelStatusAction.InactivePermanently].includes(channelStatus);
  }, [channel, isTeamChannel]);

  if (isConversationInactive) {
    return (
      <ArchivedOverlay
        isTeamChannel={isTeamChannel}
        currentClinicId={currentClinicId}
        channelMembers={channel?.channelMembers}
        channelId={channel?.id}
        channelStatusAction={channel?.channelStatus?.channelStatusAction}
      />
    );
  }

  return (
    <MessageComposerContainer isMessageComposerExpanded={isMessageComposerExpanded}>
      <MessageComposerHeader>
        {!isTeamChannel && (
          <MessageTabs>
            <LeftSide>
              <MessageTab
                onClick={(): void => setMessageType(MessageType.Message)}
                className={messageType === MessageType.Message ? 'active' : ''}
                messageType={MessageType.Message}
              >
                <Icon name="message" size={isMobile ? 'sm' : undefined} />
                {isTeamChannel ? 'Team Message' : 'Message'}
              </MessageTab>
              <MessageTab
                onClick={(): void => setMessageType(MessageType.Note)}
                className={messageType === MessageType.Note ? 'active' : ''}
                messageType={MessageType.Note}
              >
                <Icon name="pen" size={isMobile ? 'sm' : undefined} />
                Note
              </MessageTab>
            </LeftSide>
            <RightSide></RightSide>
          </MessageTabs>
        )}
        <SendAsChatCheckbox
          channelPetParents={channelPetParents}
          channelPetParentIds={channelPetParentIds}
          channelId={channelId}
          channelLastMessage={channelLastMessage}
          setSendAsChatMessage={setSendAsChatMessage}
          sendAsChatMessage={sendAsChatMessage}
          messageType={messageType}
          isTeamChannel={isTeamChannel}
          draftMessageText={draftMessageText}
          inputTextareaRef={inputTextareaRef}
        />
        {!isMobile && (
          <CornerOptions>
            <Button
              size="xs"
              variant="ghostNeutral"
              iconName={isMessageComposerExpanded ? 'arrowsIn' : 'arrowsOut'}
              onClick={handleComposerToggle}
              aria-label="Expand Message Composer"
            />
          </CornerOptions>
        )}
      </MessageComposerHeader>

      <InputArea
        className={isInputFocused ? 'active' : ''}
        messageType={messageType}
        isMobile={isMobile}
        isTeamChannel={isTeamChannel}
      >
        <MessageContainer hasAttachments={attachments.length > 0}>
          <Textarea
            spellCheck={true}
            id={ElementIds.MessageComposerTextArea}
            placeholder={messageInputPlaceholder}
            value={draftMessageText}
            ref={inputTextareaRef}
            data-testid="message-composer-textarea"
            onChange={handleDraftMessageTextChange}
            onKeyDown={(e): void => {
              const message = e.currentTarget.value;

              if (e.key === '@' && shouldShowMentionsList) {
                setMentionsListIsVisible(true);
              }
              if (
                // Send message on enter
                (e.key === 'Enter' &&
                  !e.shiftKey &&
                  !mentionsListIsVisible &&
                  !clinicUser?.userSetting?.enterAddsNewLine) ||
                // Enter adds new line and send message with shift + enter
                (e.key === 'Enter' && e.shiftKey && !mentionsListIsVisible && clinicUser?.userSetting?.enterAddsNewLine)
              ) {
                e.preventDefault();
                const shortcutRegex = /(^\/\/[a-z]{1})|( \/\/[a-z]{1} )|( \/\/[a-z]{1})$/g;

                // Support keyboard shortcuts
                if (message.match(shortcutRegex)) {
                  handleKeyboardShortcuts(message);
                  return;
                }

                handleSendMessage();
              }
            }}
            onBlur={(): void => setIsInputFocused(false)}
            onFocus={(): void => {
              setIsInputFocused(true);
            }}
          />
          {!isTeamChannel && (
            <QuickReplyPopover
              channelId={channelId}
              setDraftMessageText={setDraftMessageText}
              addSuggestion={addSuggestion}
              discardSuggestion={discardSuggestion}
              setSelectedSuggestion={setSelectedSuggestion}
            />
          )}
          {isDictationEnabled && (
            <DictationButton
              isSendingMessage={isSendingMessage}
              setMessage={setDraftMessageText}
              inputRef={inputTextareaRef}
            />
          )}
        </MessageContainer>
        {!!attachments.length && (
          <AttachmentsContainer
            onClick={(e: React.MouseEvent<Element, globalThis.MouseEvent>): void => {
              if (inputTextareaRef?.current && e.target === e.currentTarget) {
                inputTextareaRef.current.focus();
              }
            }}
          >
            <HStack m={2} spacing={3} wrap="wrap">
              {isOpenFormFieldModalOpen && (
                <OpenFormFieldModal
                  isOpen={isOpenFormFieldModalOpen}
                  onClose={onOpenFormFieldModalClose}
                  attachment={selectedFormAttachment}
                  channelDraft={channelDraft}
                  setChannelDraft={setChannelDraft}
                />
              )}
              {attachments.map((attachment: IAttachmentOption, index: number) => (
                <MessageComposerAttachment
                  key={index}
                  attachment={attachment}
                  linkedPets={linkedPets}
                  clinic={currentClinic}
                  channelDraft={channelDraft}
                  setChannelDraft={setChannelDraft}
                  onAttachmentChange={onAttachmentChange}
                  onAttachmentRemove={onAttachmentRemove}
                  setPreviewData={setPreviewData}
                  inputTextareaRef={inputTextareaRef}
                  invoiceAmountInputRef={invoiceAmountInputRef}
                  setCurrentInvoiceId={setCurrentInvoiceId}
                  setAttachmentFeePercent={setAttachmentFeePercent}
                  setHasPotentialBenefits={setHasPotentialBenefits}
                  setIsLinkPetToFormModalOpen={setIsLinkPetToFormModalOpen}
                  setSelectedFormAttachment={setSelectedFormAttachment}
                  onOpenFormFieldModalOpen={onOpenFormFieldModalOpen}
                />
              ))}
            </HStack>
          </AttachmentsContainer>
        )}
        <MessageComposerToolbar
          shortcuts={shortcuts}
          channel={channel}
          clinicPetParents={channelPetParents}
          clinic={currentClinic}
          messageType={messageType}
          attachments={attachments}
          stagedFiles={stagedFiles}
          inputTextareaRef={inputTextareaRef}
          isChannelLoading={isChannelLoading}
          isSendingMessage={isSendingMessage}
          isTeamChannel={isTeamChannel}
          isInvoicingDrawerOpen={isInvoicingDrawerOpen}
          onInvoicingDrawerClose={onInvoicingDrawerClose}
          onInvoicingDrawerToggle={onInvoicingDrawerToggle}
          setAttachments={setAttachments}
          setStagedFiles={setStagedFiles}
          setDraftMessageText={setDraftMessageText}
          onEmojiSelect={handleEmojiSelect}
          onSendMessage={handleSendMessage}
          isEmojiPickerOpen={isEmojiPickerOpen}
          setIsEmojiPickerOpen={setIsEmojiPickerOpen}
          setHasPotentialBenefits={setHasPotentialBenefits}
          hasPotentialBenefits={hasPotentialBenefits}
          setInvoiceForSidePanelId={setInvoiceForSidePanelId}
          setAttachInvoiceClicked={setAttachInvoiceClicked}
          setIsLinkPetToFormModalOpen={setIsLinkPetToFormModalOpen}
          setSelectedFormAttachment={setSelectedFormAttachment}
          setShouldOpenOpenFormModal={setShouldOpenOpenFormModal}
          isOverlay={isOverlay}
        />
        {mentionsListIsVisible && (
          <MentionsList
            isListVisibile={mentionsListIsVisible}
            setIsListVisible={setMentionsListIsVisible}
            inputTextareaRef={inputTextareaRef}
            setDraftMessageText={setDraftMessageText}
            setMentionedUsers={setMentionedUsersDraft}
          />
        )}
      </InputArea>
      <MessageComposerKeyboardShortcutHelpModal isOpen={isShortcutHelpModalOpen} onClose={closeShortcutHelpModal} />
      {attachments.length > 0 && linkedPets.length ? (
        <LinkPetToFormModal
          isOpen={isLinkPetToFormModalOpen}
          onClose={(): void => {
            setIsLinkPetToFormModalOpen(false);
          }}
          attachments={attachments}
          linkedPets={linkedPets}
          onAttachmentChange={onAttachmentChange}
        />
      ) : null}
    </MessageComposerContainer>
  );
};

const MessageComposerContainer = styled.div<{ isMessageComposerExpanded: boolean }>`
  flex: 0 0 auto;
  padding: 0 12px 12px;
  border-top: 1px solid #ddd;
  position: relative;
  width: 100%;
  height: 'unset';
  height: ${({ isMessageComposerExpanded }): string => (isMessageComposerExpanded ? '50%' : 'unset')};
`;

interface IInputAreaProps {
  messageType: MessageType;
  isMobile: boolean;
  isTeamChannel?: boolean;
}
const InputArea = styled.div<IInputAreaProps>`
  padding-bottom: 8px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  border: 1px solid var(--chakra-colors-border-default);
  border-radius: 8px;
  transition: border-color 0.1s ease-in-out;
  height: ${({ isMobile, isTeamChannel }: IInputAreaProps): string =>
    isMobile ? 'auto' : isTeamChannel ? 'calc(100% - 20px)' : 'calc(100% - 45px)'};
  background-color: ${({ messageType }: IInputAreaProps): string => {
    return messageType === MessageType.Note
      ? 'var(--chakra-colors-background-note)'
      : 'var(--chakra-colors-background-default)';
  }};

  &:hover {
    border-color: var(--chakra-colors-border-strong);
  }

  &.active {
    border-color: ${({ messageType }: IInputAreaProps): string => {
      return messageType === MessageType.Note
        ? 'var(--chakra-colors-border-warning)'
        : 'var(--chakra-colors-border-focus)';
    }};
    box-shadow: 0 0 0 1px
      ${({ messageType }: IInputAreaProps): string => {
        return messageType === MessageType.Note
          ? 'var(--chakra-colors-border-warning)'
          : 'var(--chakra-colors-border-focus)';
      }};
  }
`;

const MessageContainer = styled.div<{ hasAttachments: boolean }>`
  border-bottom: ${({ hasAttachments }): string => {
    return hasAttachments ? '1px solid var(--chakra-colors-border-default)' : 'none';
  }};
  display: flex;
  flex-direction: row;
  flex: 1;
  height: 100%;
`;

const Textarea = styled.textarea`
  background-color: transparent;
  border: none;
  font-family: inherit;
  height: 5em;
  padding: 12px;
  resize: none;
  width: 100%;
  height: 100%;
`;

const AttachmentsContainer = styled.div`
  background-color: transparent;
  display: flex;
  flex-flow: row wrap;
  padding: 12px 4px 8px;
  max-height: 170px;
  overflow-y: scroll;
`;

const MessageComposerHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 4px 8px 12px;
  @media (max-width: 500px) {
    padding-left: 0;
  }
`;

const MessageTabs = styled.div`
  flex: 1 1 auto;
  display: flex;
  border: none;
`;

interface IMessageTab {
  messageType?: string;
}

const MessageTab = styled.div<IMessageTab>`
  border: 0;
  height: 32px;
  display: flex;
  width: auto;

  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  cursor: pointer;
  align-items: center;
  border-bottom: 2px solid transparent;

  &.active {
    border-bottom-color: ${({ messageType }): string =>
      messageType === MessageType.Message ? 'var(--chakra-colors-border-info)' : 'var(--chakra-colors-border-warning)'};
  }

  svg {
    margin-right: 8px;
    @media (max-width: 500px) {
      margin-right: 4px;
    }
  }
`;

const LeftSide = styled.div`
  flex: 1 1 auto;
  display: flex;
  gap: 12px;
`;

const RightSide = styled.div`
  flex: 1 1 auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const CornerOptions = styled.div`
  margin-left: 8px;
`;

export default React.memo(MessageComposer);
